// Core
import { RefObject, useLayoutEffect } from 'react';
// Packages
import { useLocation } from 'react-router-dom';
// Modules
import { matchesMediaQuery, REDUCED_MOTION_QUERY } from 'modules/theme';
// Styles
import { MOTION } from 'styles/tokens';

// Opacity only: a transform, even for the 250 ms it runs, makes <main> the containing block of its
// position: fixed children (modals, drawers, popups), which would jump. No fill: once it ends
// nothing is left on the element.
const PAGE_ENTER_KEYFRAMES: Keyframe[] = [{ opacity: 0 }, { opacity: 1 }];

const PAGE_ENTER_TIMING: KeyframeAnimationOptions = {
  duration: MOTION.duration.slow,
  easing: MOTION.easing.out,
};

/**
 * Fades the page content in when the page changes. A query-string change (filters, the week) is
 * the same page and stays still. Nothing remounts and clicks go through while it runs.
 */
export const usePageEnterAnimation = (ref: RefObject<HTMLElement | null>) => {
  const { pathname } = useLocation();

  // A layout effect starts the fade before the browser paints the new page; a passive effect
  // would show it fully opaque for one frame first, then blink to transparent.
  useLayoutEffect(() => {
    const element = ref.current;

    // Browsers without the Web Animations API just show the page.
    if (matchesMediaQuery(REDUCED_MOTION_QUERY) || typeof element?.animate !== 'function') {
      return;
    }

    element.animate(PAGE_ENTER_KEYFRAMES, PAGE_ENTER_TIMING);
  }, [pathname, ref]);
};
